import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Zap, ChevronDown, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Hire a Court Reporter", path: "/hire" },
  { name: "Become a Court Reporter", path: "/stenou" },
  { name: "About", path: "/about" },
  { name: "FAQ", path: "/faq" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => setUser(null));
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path.split('#')[0]);

  const handleSubmenuClick = (path) => {
    if (path.includes('#')) {
      const [pagePath, hash] = path.split('#');
      if (location.pathname === pagePath) {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = path;
      }
    }
    setActiveSubmenu(null);
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className="glass-header fixed top-0 left-0 right-0 z-50"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_5887a73d-7681-4335-991d-b7eae2b5d72e/artifacts/2c98d0z6_LOGOS%20%2878%29.png"
              alt="Aym High Steno"
              className="h-8 lg:h-10 w-auto invert"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div 
                key={link.path} 
                className="relative"
                onMouseEnter={() => link.hasSubmenu && setActiveSubmenu(link.path)}
                onMouseLeave={() => setActiveSubmenu(null)}
              >
                <Link
                  to={link.path}
                  className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? "text-[#38b6ff] text-glow-cyan"
                      : "text-[#E0E0E0] hover:text-[#F5F5F7]"
                  }`}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                  {link.hasSubmenu && <ChevronDown className="w-3 h-3" />}
                </Link>

                {/* Submenu */}
                {link.hasSubmenu && activeSubmenu === link.path && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-2 w-48 bg-[#1A1A1A] border border-[#333333] shadow-xl"
                  >
                    {link.submenu.map((subItem) => (
                      <button
                        key={subItem.path}
                        onClick={() => handleSubmenuClick(subItem.path)}
                        className="block w-full text-left px-4 py-3 text-sm text-[#E0E0E0] hover:text-[#38b6ff] hover:bg-[#1A1A20] transition-colors"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {subItem.name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {user?.role === "admin" ? (
              <Link to="/admin">
                <Button
                  variant="outline"
                  className="rounded-none border-[#333333] text-[#F5F5F7] bg-transparent hover:border-[#38b6ff] hover:text-[#38b6ff] transition-all duration-300"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="dashboard-button"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button
                  variant="outline"
                  className="rounded-none border-[#333333] text-[#F5F5F7] bg-transparent hover:border-[#38b6ff] hover:text-[#38b6ff] transition-all duration-300"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="login-button"
                >
                  Login
                </Button>
              </Link>
            )}
            <Link to="/stenou#consultation">
              <Button
                className="rounded-none bg-[#38b6ff] text-[#0D0D0D] hover:bg-[#38b6ff]/90 btn-neon font-semibold"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid="book-consultation-button"
              >
                <Zap className="w-4 h-4 mr-2" />
                Start Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#E0E0E0] hover:text-[#38b6ff] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden py-4 border-t border-[#333333]" 
              data-testid="mobile-nav"
            >
              <nav className="flex flex-col space-y-2">
                {navLinks.map((link) => (
                  <div key={link.path}>
                    <Link
                      to={link.path}
                      className={`block text-sm font-medium py-2 ${
                        isActive(link.path)
                          ? "text-[#38b6ff]"
                          : "text-[#E0E0E0] hover:text-[#F5F5F7]"
                      }`}
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      onClick={() => !link.hasSubmenu && setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                    {link.hasSubmenu && (
                      <div className="pl-4 space-y-1 mt-1">
                        {link.submenu.map((subItem) => (
                          <button
                            key={subItem.path}
                            onClick={() => handleSubmenuClick(subItem.path)}
                            className="block w-full text-left text-xs py-2 text-[#E0E0E0] hover:text-[#38b6ff]"
                            style={{ fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            → {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex flex-col space-y-3 pt-4 border-t border-[#333333] mt-2">
                  {user?.role === "admin" ? (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full rounded-none border-[#333333] text-[#F5F5F7] bg-transparent"
                        data-testid="mobile-dashboard-button"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full rounded-none border-[#333333] text-[#F5F5F7] bg-transparent"
                        data-testid="mobile-login-button"
                      >
                        Login
                      </Button>
                    </Link>
                  )}
                  <Link to="/stenou#consultation" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      className="w-full rounded-none bg-[#38b6ff] text-[#0D0D0D] btn-neon"
                      data-testid="mobile-book-consultation-button"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Start Now
                    </Button>
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
